import { useEffect, useRef, useState } from 'react'

/**
 * Decorative, ambient motion. Never product proof, never telemetry.
 * - Lazy: only mounts the <video> near the viewport.
 * - Reduced motion: shows the poster frame instead of playing video.
 * - aria-hidden always: pure atmosphere, no information.
 */
export function MotionVideo({
  src,
  poster,
  className = '',
  eager = false,
}: {
  src: string
  poster: string
  className?: string
  eager?: boolean
}) {
  const hostRef = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(eager)
  const [reduceMotion, setReduceMotion] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const sync = () => setReduceMotion(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  useEffect(() => {
    if (inView) return
    const node = hostRef.current
    if (!node) return
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true)
            io.disconnect()
          }
        }
      },
      { rootMargin: '240px' },
    )
    io.observe(node)
    return () => io.disconnect()
  }, [inView])

  return (
    <div ref={hostRef} className={className} aria-hidden="true">
      {reduceMotion || !inView ? (
        <img
          src={poster}
          alt=""
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          className="h-full w-full object-cover"
        />
      ) : (
        <video
          className="h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={poster}
          disablePictureInPicture
        >
          <source src={src} type="video/mp4" />
        </video>
      )}
    </div>
  )
}

/** MotionVideo inside the corner-bracketed product frame. */
export function MotionFrame({
  src,
  poster,
  className = '',
  label,
}: {
  src: string
  poster: string
  className?: string
  label?: string
}) {
  return (
    <figure className={className}>
      <div className="shot-frame rounded-sm">
        <span aria-hidden="true" className="shot-corner tl" />
        <span aria-hidden="true" className="shot-corner tr" />
        <span aria-hidden="true" className="shot-corner bl" />
        <span aria-hidden="true" className="shot-corner br" />
        <MotionVideo
          src={src}
          poster={poster}
          className="aspect-square w-full overflow-hidden rounded-sm"
        />
      </div>
      {label ? (
        <figcaption className="mt-3 font-mono text-[11px] uppercase tracking-[0.14em] text-ink-dim">
          {label}
        </figcaption>
      ) : null}
    </figure>
  )
}
